/**
 * InvitePage — accept a workspace invitation by token. If the user is
 * logged in, we hit the API immediately. If not, we stash the token in
 * localStorage and route them to /register so they can sign up first,
 * then auto-redeem after auth.
 */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import PublicHeader from "@/components/PublicHeader";

export default function InvitePage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "pending", message: "" });

  useEffect(() => {
    if (!ready) return;
    if (!token) {
      setState({ status: "error", message: "Missing invite token" });
      return;
    }
    if (!user) {
      // Stash the token and redirect to register so they can sign up
      localStorage.setItem("auditor_pending_invite", token);
      navigate("/register", { replace: true });
      return;
    }
    (async () => {
      try {
        const r = await api.post("/workspace/accept-invite", { token });
        toast.success(`Joined "${r.data.workspace.name}"`);
        localStorage.removeItem("auditor_pending_invite");
        setState({ status: "ok", message: `You're now a member of ${r.data.workspace.name}.` });
        setTimeout(() => navigate("/app/workspace", { replace: true }), 1200);
      } catch (err) {
        setState({ status: "error", message: err?.response?.data?.detail || "Invite invalid or expired" });
      }
    })();
  }, [ready, user, token, navigate]);

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      <div className="max-w-xl mx-auto px-6 py-24">
        <div className="surface-card p-8" data-testid="invite-card">
          <div className="label-overline mb-2">Workspace invitation</div>
          <h1 className="font-display text-3xl font-black tracking-tight mb-3">
            {state.status === "pending" && "Accepting your invite…"}
            {state.status === "ok" && "You're in! 🎉"}
            {state.status === "error" && "Invite couldn't be redeemed"}
          </h1>
          <p className="text-sm text-ink-muted">{state.message || "One moment…"}</p>
        </div>
      </div>
    </div>
  );
}
